import { EyeIcon } from '@heroicons/react/24/outline';
import { useState, useMemo } from 'react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ScatterChart, Scatter } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { PageHeader } from '../components/PageHeader';
import SortableTable from '../components/SortableTable';
import Pagination from '../components/Pagination';
import AuditLogDataDetails from '../components/AuditLogDataDetails';
import { Skeleton } from '../components/Skeleton';
import { useFilterState } from '../components/filters/useFilterState';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { formatDateOnly, formatDateTime } from '../lib/dateFormat';
import AuditFiltersPanel from './audit/AuditFilters';
import { DEFAULT_AUDIT_FILTERS, SERIES_COLORS, type AuditFilters } from './audit/auditFilters.types';

const FILTER_STORAGE_KEY = 'audit-filters';

export default function Audit() {
  useDocumentTitle('Audit');

  const [page, setPage] = useState(1);

  // Persisted filter state (sessionStorage)
  const { filters, setFilters } = useFilterState<AuditFilters>({
    storageKey: FILTER_STORAGE_KEY,
    defaults: DEFAULT_AUDIT_FILTERS,
    onFilterChange: () => {
      // No debounced side-effect needed — queries derive from filters directly
    },
  });

  const { groupBy, deselectedUsers, deselectedActions } = filters;

  const {
    data: logsData,
    isLoading: logsLoading,
    isError: logsIsError,
    error: logsError,
  } = useQuery({
    queryKey: ['auditLogs', page, deselectedUsers, deselectedActions],
    queryFn: () =>
      backend
        .listAuditLogs(page, 20, {
          excludeUsernames: deselectedUsers,
          excludeActions: deselectedActions,
        })
        .then((res) => res.data),
  });

  const logs = logsData?.items ?? [];
  const hasMore = logsData?.hasMore ?? false;

  const {
    data: chartData = [],
    isLoading: chartLoading,
    isError: chartIsError,
    error: chartError,
  } = useQuery({
    queryKey: ['auditLogChart', groupBy],
    queryFn: () => backend.getAuditLogActivityChart({ groupBy }).then((res) => res.data.data),
  });

  // The chart endpoint swaps dimensions with group_by: under groupBy=username the
  // outer `action` field holds actions and `data[i][0]` holds usernames; under
  // groupBy=action they are inverted. Route each dimension to its matching store
  // so the persisted exclusions stay valid for listAuditLogs.
  const seriesDeselected = groupBy === 'action' ? deselectedActions : deselectedUsers;
  const sumDeselected = groupBy === 'action' ? deselectedUsers : deselectedActions;

  const { chartArray, sumSet, seriesSet } = useMemo(() => {
    const sumSet = Array.from(new Set(chartData.map((item) => item.action))).sort();
    const seriesSet = Array.from(new Set(chartData.flatMap((item) => item.data.map((d) => d[0])))).sort();

    const chartDataByDay: Record<string, Record<string, number>> = {};

    chartData.forEach((dataItem) => {
      const day = dataItem.date.slice(0, 10);

      dataItem.data.forEach((item) => {
        if (sumDeselected.includes(dataItem.action)) return;
        if (seriesDeselected.includes(item[0])) return;

        const series = item[0];
        chartDataByDay[day] ??= {};
        chartDataByDay[day][series] ??= 0;
        chartDataByDay[day][series] += item[1];
      });
    });

    const chartArray = Object.entries(chartDataByDay).map(([date, seriesCounts]) => ({
      date,
      ...seriesCounts,
    }));

    return { sumSet, seriesSet, chartArray };
  }, [chartData, sumDeselected, seriesDeselected]);

  const visibleSeries = seriesSet.filter((s) => !seriesDeselected.includes(s));
  const chartIsEmpty = !chartLoading && chartArray.length === 0;

  return (
    <div>
      <PageHeader title="Audit" description="Activity audit" icon={<EyeIcon className="size-6" />} />

      {/* Chart card */}
      <div className="mb-8 bg-white dark:bg-zinc-800 rounded-lg shadow p-4">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-zinc-100 mb-4">User Activity Over Time</h2>

        <div className="grid grid-cols-12 gap-4">
          {/* Filter panel */}
          <div className="col-span-2">
            <AuditFiltersPanel
              filters={filters}
              setFilters={setFilters}
              seriesSet={seriesSet}
              sumSet={sumSet}
            />
          </div>

          {/* Chart area */}
          <div className="col-span-10 min-h-64">
            {chartLoading ? (
              <div className="h-64 flex flex-col gap-3 justify-center px-4">
                <Skeleton variant="rect" className="h-full w-full" />
              </div>
            ) : chartIsError ? (
              <div className="h-64 flex items-center justify-center text-sm text-red-600 dark:text-red-400">
                {chartError instanceof Error ? chartError.message : 'Failed to load audit activity.'}
              </div>
            ) : chartIsEmpty ? (
              <div className="h-64 flex items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
                No audit events for the selected filters.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart accessibilityLayer={false} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    type="category"
                    fontSize={12}
                    allowDuplicatedCategory={false}
                    tickFormatter={(value) => formatDateOnly(new Date(`${value}T12:00:00`))}
                  />
                  <YAxis allowDecimals={false} allowDuplicatedCategory={false} />
                  <Tooltip />
                  {visibleSeries.map((item) => (
                    <Scatter
                      key={item}
                      name={item}
                      data={chartArray.map((row) => ({
                        date: row.date,
                        value: (row as Record<string, unknown>)[item] ?? 0,
                      }))}
                      dataKey="value"
                      fill={SERIES_COLORS[seriesSet.indexOf(item) % SERIES_COLORS.length]}
                      line={true}
                    />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg">
        {logsLoading ? (
          <div className="bg-white dark:bg-zinc-800 rounded-lg p-4">
            <Skeleton variant="rect" lines={5} />
          </div>
        ) : logsIsError ? (
          <div className="bg-white dark:bg-zinc-800 rounded-lg p-8 text-center text-sm text-red-600 dark:text-red-400">
            {logsError instanceof Error ? logsError.message : 'Failed to load audit events.'}
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white dark:bg-zinc-800 rounded-lg p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
            No audit events for the selected filters.
          </div>
        ) : (
          <SortableTable
            storageId="audit-log"
            limitHeight={false}
            columns={[
              {
                accessor: 'createdAt',
                label: 'Time',
                render: (row) => formatDateTime(row.createdAt),
              },
              {
                accessor: 'username',
                label: 'User',
                render: (row) => row.username || row.userId,
              },
              {
                accessor: 'details',
                label: 'Details',
                render: (row) => <AuditLogDataDetails data={row} />,
              },
            ]}
            data={logs}
            rowKey={(row) => row.id}
          />
        )}
        <Pagination page={page} handlePageChange={setPage} hasNextPage={hasMore} />
      </div>
    </div>
  );
}
